import { Alert, Box, FormLabel, Typography } from '@mui/material';
import * as React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CurrencyField } from '~/components/GenericForm/CurrencyField';
import { PercentageField } from '~/components/GenericForm/PercentageField';
import { useSalaryRaiseFields } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/SalaryProposalForm/components/SalaryRaiseFields/useSalaryRaiseFields';
import {
  CurrentEmploymentSalary,
  SalaryProposalFormData,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/SalaryProposalForm/data';

import { useSxStyles } from '../../../Common/useStyles';

type PropsType = {
  form: UseFormReturn<SalaryProposalFormData>;
  currency: string;
  currentEmploymentSalary: CurrentEmploymentSalary;
  disabled?: boolean;
};

export const SalaryRaiseFields = (props: PropsType) => {
  const { form, currency, currentEmploymentSalary, disabled } = props;
  const { control } = form;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    onRaiseFieldChange,
    getFrequencyEnumName,
    showRaiseFields,
    isCurrentSalaryFrequency,
    hasCurrentSalary,
  } = useSalaryRaiseFields({
    form,
    currentEmploymentSalary,
  });

  const CurrentSalary = () => {
    if (!hasCurrentSalary) {
      return (
        <Alert severity={'info'}>{t('noCurrentSalaryOnEmployment')}</Alert>
      );
    }

    if (!isCurrentSalaryFrequency) {
      const currentSalaryInfo = `${t('currently')}: ${getFrequencyEnumName(
        currentEmploymentSalary.salaryFrequency,
      )} ${currentEmploymentSalary.baseSalary.toFixed(2)} ${currency}`;

      return <Typography>{currentSalaryInfo}</Typography>;
    }

    return (
      <Controller
        name={'baseSalary'}
        control={control}
        render={({ field: { value, onChange } }) => {
          return (
            <CurrencyField
              label={t('laBaseSalary')}
              value={value}
              onChange={(e) => onRaiseFieldChange(e, onChange, 'baseSalary')}
              currency={currency}
              disabled={true}
            />
          );
        }}
      />
    );
  };

  return (
    <>
      <CurrentSalary />

      {showRaiseFields && (
        <Box sx={sxStyles.fieldSet}>
          <FormLabel>{t('laRaise')}</FormLabel>
          <Box sx={sxStyles.fieldRow}>
            <Controller
              name={'raiseAmount'}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <CurrencyField
                    label={t('laRaiseAmount')}
                    value={value}
                    onChange={(e) =>
                      onRaiseFieldChange(e, onChange, 'raiseAmount')
                    }
                    currency={currency}
                    required={true}
                    disabled={disabled}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                );
              }}
            />
            <Controller
              name={'raisePercentage'}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <PercentageField
                    label={t('laRaisePercentage')}
                    value={value}
                    onChange={(e) =>
                      onRaiseFieldChange(e, onChange, 'raisePercentage')
                    }
                    required={true}
                    disabled={disabled}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Box>
        </Box>
      )}

      <Controller
        name={'newSalary'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <CurrencyField
              label={t('laNewSalary')}
              value={value}
              onChange={(e) => onRaiseFieldChange(e, onChange, 'newSalary')}
              currency={currency}
              required={true}
              disabled={disabled}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
    </>
  );
};
